import React, { useState, useEffect } from 'react';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';
import { RADIO_BUTTON_ANIMATION_DELAY } from '@/components/animations/transitions';

export interface AnimatedSelectionOption<T extends string> {
  value: T;
  label: string;
  icon?: React.ReactNode;
  emoji?: string;
}

interface AnimatedSelectionGroupProps<T extends string> {
  options: AnimatedSelectionOption<T>[];
  value: T | null;
  onChange: (value: T) => void;
  name: string;
  onComplete?: () => void;
  className?: string;
}

const pulseAnimation = {
  scale: [1, 1.6, 0.92, 1.15, 1],
  transition: {
    duration: 0.3,
    ease: "easeInOut",
    times: [0, 0.3, 0.5, 0.8, 1]
  }
};

const AnimatedSelectionGroup = <T extends string>({ 
  options, 
  value, 
  onChange, 
  name,
  onComplete,
  className 
}: AnimatedSelectionGroupProps<T>) => {
  const [selectedValue, setSelectedValue] = useState<T | null>(value);
  const [shouldAnimate, setShouldAnimate] = useState(false);

  // Only update local state when value prop changes and local state is null
  useEffect(() => {
    if (selectedValue === null && value !== null) {
      setSelectedValue(value);
    }
  }, [value, selectedValue]);

  const handleSelect = (newValue: T) => {
    // Always trigger animation and callbacks, even if selecting the same value
    setSelectedValue(newValue);
    setShouldAnimate(true);
    // Use the consistent animation delay
    setTimeout(() => {
      onChange(newValue);
      setShouldAnimate(false);
      onComplete?.();
    }, RADIO_BUTTON_ANIMATION_DELAY);
  };

  return (
    <div className={cn("grid grid-cols-2 lg:grid-cols-4 gap-2 py-2", className)}>
      {options.map((option) => (
        <div key={option.value}>
          <motion.button
            type="button"
            onClick={() => handleSelect(option.value)}
            animate={selectedValue === option.value && shouldAnimate ? pulseAnimation : {}}
            className={cn(
              "w-full flex flex-col items-center justify-center h-24 p-2 rounded-2xl cursor-pointer border-2 transition-all border",
              "hover:border-primary/30 hover:bg-primary/5",
              selectedValue === option.value
                ? "border-primary bg-primary/5 shadow-sm text-primary"
                : "border-border bg-background text-muted-foreground/90",
              shouldAnimate && "pointer-events-none opacity-70"
            )}
            disabled={shouldAnimate}
          >
            <div className="flex flex-col items-center mb-2">
              {option.icon && option.icon}
              {option.emoji && <span className="text-2xl">{option.emoji}</span>}
            </div>
            <span className={cn(
              "text-sm font-medium transition-colors duration-300",
              selectedValue === option.value ? "text-primary" : "text-muted-foreground/80"
            )}>{option.label}</span>
          </motion.button>
        </div>
      ))}
    </div>
  );
};

export default AnimatedSelectionGroup; 